import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { ArrowLeft, ArrowRight, Trash2, Check, X, Info, AlertTriangle } from 'lucide-react';
import { PortfolioImage } from '@/hooks/useProfile';
import { useSafeImageUrl } from '@/hooks/image-upload/useSafeImageUrl';
import AddImageCard, { AddImageCardHandle } from './AddImageCard';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';

interface ImageGalleryProps {
  images: PortfolioImage[];
  editingImageId: string | null;
  addImageCardRef: React.RefObject<AddImageCardHandle>;
  onEditImage: (id: string | null) => void;
  onRemoveImage: (id: string) => void;
  onMoveImage: (id: string, direction: 'left' | 'right') => void;
  onUpdateImageCaption: (id: string, caption: string) => void;
  onAddImages: (urls: string[]) => void;
  onUploadStatusChange?: (uploading: boolean) => void;
}

const ImageItem = ({
  image,
  index,
  totalImages,
  isEditing,
  onEdit,
  onSave,
  onCancel,
  onRemove,
  onMove,
  onCaptionChange,
  editedCaption
}: {
  image: PortfolioImage;
  index: number;
  totalImages: number;
  isEditing: boolean;
  onEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
  onRemove: () => void;
  onMove: (direction: 'left' | 'right') => void;
  onCaptionChange: (caption: string) => void;
  editedCaption: string;
}) => {
  // Use safe image loading with repair capability
  const { safeUrl, isLoading, error, wasRepaired } = useSafeImageUrl(image.url, {
    fallback: '/placeholder-image.svg',
    enableRepair: true,
    timeout: 5 * 60 * 1000
  });
  
  // Check if this is a legacy image
  const isLegacy = image.isLegacy || image.id?.startsWith('legacy-');
  
  // Check if the image is a data URL
  const isDataUrl = typeof image.url === 'string' && image.url.startsWith('data:');
  
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState(false);
  
  return (
    <Card className="overflow-hidden">
      <div className="aspect-video relative overflow-hidden bg-muted">
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100 animate-pulse">
            <span className="text-sm text-gray-500">Loading...</span>
          </div>
        )}
        
        {safeUrl && (
          <img
            src={safeUrl}
            alt={image.caption || `Image ${index + 1}`}
            className="w-full h-full object-cover"
            onError={(e) => {
              // Add visual indicator for failed image loads
              console.error(`Image load error for ${image.id}:`, e);
              e.currentTarget.style.display = 'none';
            }}
          />
        )}
        
        {error && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-red-50">
            <AlertTriangle className="h-6 w-6 text-red-500 mb-2" />
            <span className="text-sm text-red-600">Failed to load image</span>
          </div>
        )}
        
        {isLegacy && (
          <div className="absolute top-2 left-2 bg-amber-500 bg-opacity-90 text-white text-xs px-2 py-1 rounded flex items-center">
            <Info className="h-3 w-3 mr-1" />
            Legacy format
          </div>
        )}
        
        {isDataUrl && (
          <div className="absolute top-2 left-2 bg-blue-500 bg-opacity-90 text-white text-xs px-2 py-1 rounded flex items-center">
            <Info className="h-3 w-3 mr-1" />
            Temporary
          </div>
        )}
        
        {/* {wasRepaired && (
          <div className="absolute bottom-2 right-2 bg-green-500 bg-opacity-90 text-white text-xs px-2 py-1 rounded flex items-center">
            <Info className="h-3 w-3 mr-1" />
            Fixed
          </div>
        )} */}
      </div>
      
      <CardContent className="p-3">
        {isEditing ? (
          <div className="space-y-2">
            <Input
              placeholder="Add caption..."
              value={editedCaption}
              onChange={(e) => onCaptionChange(e.target.value)}
              className="text-sm"
            />
            <div className="flex justify-end space-x-2">
              <Button size="sm" variant="outline" onClick={onCancel} className="h-8 w-8 p-0">
                <X className="h-4 w-4" />
              </Button>
              <Button size="sm" onClick={onSave} className="h-8 w-8 p-0">
                <Check className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <button
              onClick={onEdit}
              className="text-sm truncate text-left w-full hover:text-gray-600 cursor-pointer transition-colors"
            >
              {image.caption || <span className="text-gray-400">Add caption</span>}
            </button>
            <div className="flex justify-between items-center">
              <div className="space-x-1">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onMove('left')}
                  disabled={index === 0}
                  className="h-8 w-8 p-0"
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onMove('right')}
                  disabled={index === totalImages - 1}
                  className="h-8 w-8 p-0"
                >
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsDeleteDialogOpen(true);
                }}
                className="flex items-center justify-center w-6 h-6 rounded-full transition-colors bg-gray-100 border-[1px] border-gray-300 hover:bg-red-100 hover:border-red-500 cursor-pointer group"
                title="Remove image"
              >
                <Trash2 className="h-3 w-3 text-gray-500 group-hover:text-red-500 transition-colors" />
              </button>
            </div>
          </div>
        )}
      </CardContent>
      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Image</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this image? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setIsDeleteDialogOpen(false)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-white hover:bg-destructive/90"
              onClick={() => {
                onRemove();
                setIsDeleteDialogOpen(false);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

const ImageGallery = ({
  images,
  editingImageId,
  addImageCardRef,
  onEditImage,
  onRemoveImage,
  onMoveImage,
  onUpdateImageCaption,
  onAddImages,
  onUploadStatusChange,
}: ImageGalleryProps) => {
  // Use a per-image edited caption state
  const [editedCaptions, setEditedCaptions] = React.useState<{ [id: string]: string }>({});
  const [uploadPreviews, setUploadPreviews] = React.useState<string[]>([]);

  // When entering edit mode, initialize the edited caption for that image
  React.useEffect(() => {
    if (editingImageId) {
      const image = images.find((img) => img.id === editingImageId);
      if (image) {
        setEditedCaptions((prev) => ({
          ...prev,
          [editingImageId]: prev[editingImageId] ?? image.caption ?? '',
        }));
      }
    }
  }, [editingImageId, images]);

  // Handler to add images and show previews
  const handleAddImages = (urls: string[]) => {
    // Remove previews that match the uploaded URLs
    setUploadPreviews((prev) => prev.filter((preview) => !urls.includes(preview)));
    onAddImages(urls);
  };

  // Handler for preview (called before upload starts)
  const handlePreview = (previews: string[]) => {
    setUploadPreviews((prev) => [...prev, ...previews]);
  };

  // Handler for caption input change
  const handleCaptionChange = (id: string, value: string) => {
    setEditedCaptions((prev) => ({ ...prev, [id]: value }));
  };

  // Handler for saving caption
  const handleSave = (id: string) => {
    const caption = editedCaptions[id] ?? '';
    onUpdateImageCaption(id, caption);
    onEditImage(null);
  };

  // Handler for canceling edit
  const handleCancel = (id: string) => {
    onEditImage(null);
    // Optionally, clear the edited caption for this image
    // setEditedCaptions((prev) => { const copy = { ...prev }; delete copy[id]; return copy; });
  };

  return (
    <div className="flex flex-col gap-4 md:grid md:grid-cols-3 md:gap-4">
      {/* Upload card as the first card */}
      <AddImageCard
        ref={addImageCardRef}
        onImageUrlChange={handleAddImages}
        onCancel={() => {}}
        onPreview={handlePreview}
        onUploadStatusChange={onUploadStatusChange}
        variant="compact"
      />
      {/* Render the rest of the images */}
      {images.map((image, index) => (
        <ImageItem
          key={image.id}
          image={image}
          index={index}
          totalImages={images.length}
          isEditing={editingImageId === image.id}
          onEdit={() => onEditImage(image.id)}
          onSave={() => handleSave(image.id)}
          onCancel={() => handleCancel(image.id)}
          onRemove={() => onRemoveImage(image.id)}
          onMove={(direction) => onMoveImage(image.id, direction)}
          onCaptionChange={(value) => handleCaptionChange(image.id, value)}
          editedCaption={editedCaptions[image.id] ?? image.caption ?? ''}
        />
      ))}
      {/* Show uploading previews after the existing images */}
      {uploadPreviews.map((preview, idx) => (
        <Card key={preview} className="overflow-hidden">
          <div className="aspect-video relative overflow-hidden bg-muted">
            <img
              src={preview}
              alt={`Uploading Preview ${idx + 1}`}
              className="w-full h-full object-cover opacity-60"
            />
            <div className="absolute inset-0 bg-black/30 flex flex-col items-center justify-center text-white p-4">
              <p className="text-sm mb-2">Uploading...</p>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
};

export default ImageGallery;
